import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CreditCard, Globe2, Repeat, Wallet, Link2, ArrowRight, ArrowLeft, Check, ShieldCheck, ClipboardCheck, FileSearch, Plug, Rocket, Loader2, Building2, Store, CloudCog, Layers, ShieldAlert, Landmark } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { setSeo, setJsonLd, clearJsonLd, orgJsonLd, SITE_URL } from "@/lib/seo";
import { useSiteConfig } from "@/context/SiteConfigContext";

const PAGE_URL = SITE_URL + "/payment-gateway/";

const SERVICES = [
  { icon: ClipboardCheck, title: "Payment Gateway Selection", body: "We help you shortlist payment gateways and merchant providers that fit your business model, markets and currencies — based on each provider's published criteria, not guesswork." },
  { icon: FileSearch, title: "Merchant Account & Onboarding", body: "We prepare and organise your application and compliance documentation, and support you through each provider's KYC and underwriting review." },
  { icon: Plug, title: "Payment Gateway Integration", body: "We coordinate the integration plan with your chosen provider and your developers. Technical implementation scope is agreed separately for each project." },
  { icon: Globe2, title: "International Payment Solutions", body: "For cross-border businesses, we help structure multi-currency acceptance and settlement approaches that match where your customers actually are." },
];

const MODELS = [
  { icon: Store, title: "E-Commerce", body: "Card and alternative payment acceptance for online stores, aligned to your storefront platform and shipping markets." },
  { icon: Repeat, title: "SaaS & Subscription", body: "Recurring billing and subscription payment setups, subject to provider approval for your plan structure." },
  { icon: CloudCog, title: "Digital Services", body: "Payment acceptance for agencies, consultants and digital product businesses serving clients across borders." },
  { icon: Globe2, title: "International Businesses", body: "Multi-market payment setups for companies selling or operating in more than one country or currency." },
  { icon: Layers, title: "Marketplaces & Platforms", body: "Guidance on payment structures for marketplaces and platforms, including the additional provider review these models involve." },
  { icon: ShieldAlert, title: "Regulated & Higher-Risk Businesses", body: "Honest assessment for businesses in regulated or higher-risk sectors — availability depends entirely on provider risk policies." },
];

const STEPS = [
  { icon: ClipboardCheck, title: "Business Assessment", body: "You tell us about your business model, markets and payment requirements through the assessment form below." },
  { icon: FileSearch, title: "Payment Provider Matching", body: "Our team reviews your profile against provider criteria and shortlists suitable options for your case." },
  { icon: FileSearch, title: "Application Preparation", body: "We organise the documentation and information providers typically require for underwriting." },
  { icon: Building2, title: "Merchant Onboarding", body: "We support you through the provider's KYC and approval process. Approval is always the provider's decision." },
  { icon: Plug, title: "Technical Integration", body: "We coordinate the integration plan with your provider and developers. Implementation scope is agreed separately." },
  { icon: Rocket, title: "Testing & Go-Live", body: "You test transactions in the provider's sandbox or test mode before going live with real payments." },
];

const METHODS = [
  { icon: CreditCard, title: "Credit & Debit Cards", body: "Major card schemes where supported by your provider and market." },
  { icon: Wallet, title: "Alternative Payment Methods", body: "Wallets and local payment methods, depending on provider and customer location." },
  { icon: Repeat, title: "Recurring Payments", body: "Subscription and repeat billing where your provider approves it for your model." },
  { icon: Globe2, title: "Multi-Currency Payments", body: "Acceptance and settlement in multiple currencies, subject to provider coverage." },
  { icon: Link2, title: "Payment Links", body: "Shareable payment links for invoices and remote sales, where your provider offers them." },
];

const WHY = [
  "Independent, business-first guidance — we are not a reseller for any single provider",
  "Compliance documentation prepared to each provider's requirements",
  "One coordination point across provider selection, onboarding and integration planning",
  "Straight answers on eligibility — we do not promise approvals we cannot control",
];

const BUSINESS_MODELS = ["E-Commerce", "SaaS & Subscription", "Digital Services", "International Business", "Marketplace or Platform", "Regulated or Higher-Risk Business", "Other"];
const SUPPORT_OPTIONS = ["Provider selection", "Merchant onboarding", "Integration support", "Existing gateway issue"];
const PLATFORMS = ["Shopify", "WooCommerce", "Custom website or app", "Other", "Not sure"];
const CAPABILITIES = ["Credit & Debit Cards", "Alternative Payment Methods", "Recurring Payments", "Multi-Currency Payments", "Payment Links"];
const VOLUMES = ["Not sure", "Under $10,000", "$10,000–$50,000", "$50,000–$250,000", "Over $250,000"];
const VOLUME_CURRENCIES = ["USD", "EUR", "GBP", "HKD", "SGD", "AUD", "Other"];

const FALLBACK_DISCLAIMER = "Payment provider availability and approval are subject to the requirements, compliance policies and risk assessment of each payment provider. UNICORP does not guarantee account approval, processing limits, transaction fees or continued availability of any third-party payment service.";

const EMPTY_FORM = {
  registration_jurisdiction: "", business_model: "", support_required: [], business_activity: "",
  company_name: "", website_url: "", website_not_live: false, target_markets: "", currencies: "",
  monthly_volume: "", volume_currency: "USD", platform: "", existing_provider: "", capabilities: [],
  name: "", email: "", country: "", phone: "", message: "", hp: "",
};

export default function PaymentGateway() {
  const navigate = useNavigate();
  const { config } = useSiteConfig();
  const disclaimer = (config?.payment_gateway_disclaimer || "").trim() || FALLBACK_DISCLAIMER;

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY_FORM);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [jurisdictions, setJurisdictions] = useState([]);
  const assessRef = useRef(null);
  const stepHeadRef = useRef(null);

  useEffect(() => {
    setSeo({
      title: "Payment Gateway Solutions | Payment Gateway Integration | UNICORP",
      description: "Explore payment gateway solutions for international businesses. UNICORP assists with payment provider selection, merchant onboarding, compliance documentation and integration support.",
      url: PAGE_URL,
    });
    setJsonLd("org-schema", orgJsonLd());
    setJsonLd("pg-service", {
      "@context": "https://schema.org", "@type": "Service",
      name: "Payment Gateway Solutions",
      description: "Payment provider selection, merchant onboarding support and payment gateway integration coordination for international businesses.",
      provider: { "@type": "Organization", name: "UNICORP Services Limited" },
      areaServed: "Worldwide", url: PAGE_URL,
    });
    setJsonLd("pg-breadcrumb", {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
        { "@type": "ListItem", position: 2, name: "Bank Account", item: SITE_URL + "/banks" },
        { "@type": "ListItem", position: 3, name: "Payment Gateway Solutions", item: PAGE_URL },
      ],
    });
    api.get("/catalog").then((r) => setJurisdictions((r.data.countries || []).map((c) => c.name))).catch(() => {});
    return () => { clearJsonLd("pg-service"); clearJsonLd("pg-breadcrumb"); };
  }, []);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const toggleArr = (k, v) => setForm((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] }));

  const scrollToAssessment = () => {
    assessRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => stepHeadRef.current?.focus({ preventScroll: true }), 500);
  };

  const goStep = (n) => {
    setError("");
    setStep(n);
    setTimeout(() => stepHeadRef.current?.focus({ preventScroll: true }), 50);
  };

  const validateStep1 = () => {
    if (!form.registration_jurisdiction) return "Please select your company registration jurisdiction (or “Not incorporated yet”).";
    if (!form.business_model) return "Please select your business model.";
    if (form.support_required.length === 0) return "Please select at least one type of support required.";
    if (!form.business_activity.trim()) return "Please briefly describe your business activity.";
    return "";
  };
  const validateStep2 = () => {
    if (!form.name.trim()) return "Please enter your full name.";
    if (!form.email.includes("@")) return "Please enter a valid email address.";
    if (!form.country.trim()) return "Please enter your country of residence.";
    if (!consent) return "Please agree to the Privacy Policy to submit your assessment request.";
    return "";
  };

  const next = () => { const e = validateStep1(); if (e) return setError(e); goStep(2); };

  const submit = async (ev) => {
    ev.preventDefault();
    const e1 = validateStep1(); if (e1) { goStep(1); setError(e1); return; }
    const e2 = validateStep2(); if (e2) return setError(e2);
    setBusy(true);
    setError("");
    try {
      const u = new URLSearchParams(window.location.search);
      await api.post("/contact", {
        name: form.name.trim(), email: form.email.trim().toLowerCase(), phone: form.phone.trim(),
        message: form.message.trim(), type: "payment_gateway", consent,
        service: "payment_gateway_solutions",
        country_residence: form.country.trim(),
        hp: form.hp,
        source_url: window.location.href,
        utm_source: u.get("utm_source") || "", utm_medium: u.get("utm_medium") || "", utm_campaign: u.get("utm_campaign") || "",
        assessment: {
          registration_jurisdiction: form.registration_jurisdiction,
          business_model: form.business_model,
          support_required: form.support_required,
          business_activity: form.business_activity.trim(),
          company_name: form.company_name.trim(),
          website_url: form.website_not_live ? "" : form.website_url.trim(),
          website_not_live: form.website_not_live,
          target_markets: form.target_markets.trim(),
          currencies: form.currencies.trim(),
          monthly_volume: form.monthly_volume,
          volume_currency: form.volume_currency,
          platform: form.platform,
          existing_provider: form.existing_provider.trim(),
          capabilities: form.capabilities,
        },
      });
      setDone(true);
      assessRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(err?.response?.status === 429
        ? "Too many submissions — please try again later or WhatsApp us."
        : (typeof d === "string" ? d : "Something went wrong. Your answers are saved on this page — please try again, or contact us on WhatsApp."));
    } finally { setBusy(false); }
  };

  const reviewRows = [
    ["Registration jurisdiction", form.registration_jurisdiction],
    ["Business model", form.business_model],
    ["Support required", form.support_required.join(", ")],
    ["Business activity", form.business_activity],
    ...(form.company_name ? [["Company", form.company_name]] : []),
    ...(form.website_not_live ? [["Website / app", "Not live yet"]] : form.website_url ? [["Website / app", form.website_url]] : []),
    ...(form.target_markets ? [["Target markets", form.target_markets]] : []),
    ...(form.currencies ? [["Currencies", form.currencies]] : []),
    ...(form.monthly_volume ? [["Monthly volume", form.monthly_volume + (form.monthly_volume !== "Not sure" ? ` ${form.volume_currency}` : "")]] : []),
    ...(form.platform ? [["Platform", form.platform]] : []),
    ...(form.existing_provider ? [["Existing / preferred provider", form.existing_provider]] : []),
    ...(form.capabilities.length ? [["Payment capabilities", form.capabilities.join(", ")]] : []),
  ];

  const labelCls = "text-xs font-medium text-muted-foreground uppercase tracking-wider";
  const inputCls = "mt-2 bg-background border-border";

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative grain pt-32 pb-14 border-b border-border bg-secondary/40">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-orange-600 font-mono"><CreditCard className="h-3.5 w-3.5" /> Payment Solutions</p>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight text-primary" data-testid="pg-title">Payment Gateway Solutions</h1>
            <p className="mt-4 text-muted-foreground text-lg leading-relaxed max-w-2xl">Payment infrastructure for your international business.</p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button size="lg" className="rounded-sm bg-orange-600 text-white hover:bg-orange-700 h-12 px-7" onClick={scrollToAssessment} data-testid="pg-cta-hero">Get Started <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
            </div>
            <p className="mt-4 flex items-start gap-2 text-xs text-muted-foreground max-w-xl"><ShieldCheck className="h-3.5 w-3.5 text-orange-600 mt-0.5 shrink-0" /> Provider availability and approval depend on your business profile and the provider’s compliance review.</p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-14 space-y-12">
        <div>
          <h2 className="font-heading text-2xl tracking-tight text-primary">Find the Right Payment Solution for Your Business</h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Accepting payments internationally means choosing the right payment gateway and merchant provider for your business model, markets and risk profile. Every provider applies its own compliance and underwriting criteria — so the right starting point is an honest assessment of your requirements, not a generic checkout button. UNICORP helps you navigate that choice and prepares you for each provider’s review.
          </p>
        </div>

        <div>
          <h2 className="font-heading text-2xl tracking-tight text-primary">Our Payment Gateway Services</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            {SERVICES.map((s) => (
              <div key={s.title} className="rounded-sm border border-border bg-card p-5" data-testid={`pg-service-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <span className="grid place-items-center h-9 w-9 rounded-sm bg-orange-50 text-orange-600 border border-orange-100"><s.icon className="h-5 w-5" /></span>
                <h3 className="mt-3 font-heading text-lg text-primary">{s.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-heading text-2xl tracking-tight text-primary">Solutions for Different Business Models</h2>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {MODELS.map((m) => (
              <div key={m.title} className="rounded-sm border border-border bg-card p-4" data-testid={`pg-model-${m.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-center gap-2.5"><m.icon className="h-4 w-4 text-orange-600 shrink-0" /><h3 className="text-sm font-medium text-primary">{m.title}</h3></div>
                <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{m.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-heading text-2xl tracking-tight text-primary">How It Works</h2>
          <div className="mt-4 space-y-3">
            {STEPS.map((s, i) => (
              <div key={s.title} className="flex gap-4 rounded-sm border border-border bg-card p-4">
                <span className="grid place-items-center h-8 w-8 rounded-sm bg-orange-50 text-orange-600 border border-orange-100 shrink-0 font-mono text-sm">{i + 1}</span>
                <div><p className="text-sm font-medium text-primary">{s.title}</p><p className="mt-1 text-sm text-muted-foreground leading-relaxed">{s.body}</p></div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-heading text-2xl tracking-tight text-primary">Payment Methods</h2>
          <p className="mt-2 text-sm text-muted-foreground">The payment methods available to you depend on the selected provider, your market and your business eligibility.</p>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {METHODS.map((m) => (
              <div key={m.title} className="flex items-start gap-3 rounded-sm border border-border bg-card p-4" data-testid={`pg-method-${m.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <m.icon className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
                <div><p className="text-sm font-medium text-primary">{m.title}</p><p className="mt-1 text-xs text-muted-foreground leading-relaxed">{m.body}</p></div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="font-heading text-2xl tracking-tight text-primary">Why Work with UNICORP?</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            {WHY.map((w) => (
              <div key={w} className="flex items-start gap-3 rounded-sm border border-border bg-card p-4"><Check className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" /><span className="text-sm text-foreground">{w}</span></div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA + Assessment */}
      <section id="assessment" ref={assessRef} className="border-t border-border bg-secondary/40 grain scroll-mt-24" data-testid="pg-assessment">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14">
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-primary">Looking for the Right Payment Gateway?</h2>
          <p className="mt-3 text-muted-foreground leading-relaxed max-w-2xl">Tell us about your business and payment requirements. Our team will review your answers and contact you about the next steps — no login required.</p>
          <p className="mt-3 flex items-start gap-2 text-xs text-muted-foreground max-w-xl"><ShieldCheck className="h-3.5 w-3.5 text-orange-600 mt-0.5 shrink-0" /> Provider availability and approval depend on your business profile and the provider’s compliance review.</p>

          <div className="mt-8 rounded-2xl border border-border bg-card p-6 sm:p-8 card-soft">
            {done ? (
              <div className="text-center py-8" data-testid="pg-success">
                <span className="mx-auto grid place-items-center h-12 w-12 rounded-full bg-green-50 text-green-600 border border-green-200"><Check className="h-6 w-6" /></span>
                <h3 className="mt-4 font-heading text-xl text-primary">Thank you. Your payment assessment request has been received.</h3>
                <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">Our team will review your requirements and contact you about the next steps.</p>
                <p className="mt-4 text-xs text-muted-foreground max-w-md mx-auto">If supporting documents are needed, we will request them later through our existing secure document process.</p>
                <Button variant="outline" className="mt-6 rounded-sm border-slate-300 bg-card hover:bg-secondary" onClick={() => navigate("/banks")} data-testid="pg-success-browse"><Landmark className="h-4 w-4 mr-1.5 text-primary" /> Explore banking options</Button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate>
                {/* Step indicator */}
                <div className="flex items-center gap-3 mb-7" role="progressbar" aria-valuenow={step} aria-valuemin={1} aria-valuemax={2} aria-label="Form progress" data-testid="pg-step-indicator">
                  {[1, 2].map((n) => (
                    <React.Fragment key={n}>
                      <button type="button" onClick={() => n === 1 ? goStep(1) : next()} className={`flex items-center gap-2 text-xs font-mono uppercase tracking-wider ${step === n ? "text-orange-600" : "text-muted-foreground"}`} data-testid={`pg-step-${n}`}>
                        <span className={`grid place-items-center h-6 w-6 rounded-full border text-[11px] ${step === n ? "bg-orange-600 text-white border-orange-600" : "border-border"}`}>{n}</span>
                        {n === 1 ? "Requirements" : "Contact & review"}
                      </button>
                      {n === 1 && <span className="h-px flex-1 bg-border" />}
                    </React.Fragment>
                  ))}
                </div>
                <h3 ref={stepHeadRef} tabIndex={-1} className="font-heading text-lg text-primary outline-none" data-testid="pg-step-heading">
                  {step === 1 ? "Step 1 — Business & Payment Requirements" : "Step 2 — Contact & Review"}
                </h3>

                {step === 1 && (
                  <div className="mt-6 space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="pg-jurisdiction" className={labelCls}>Company registration jurisdiction *</label>
                        <Select value={form.registration_jurisdiction} onValueChange={set("registration_jurisdiction")}>
                          <SelectTrigger id="pg-jurisdiction" className={inputCls} data-testid="pg-jurisdiction"><SelectValue placeholder="Select jurisdiction" /></SelectTrigger>
                          <SelectContent className="max-h-72">
                            <SelectItem value="Not incorporated yet">Not incorporated yet</SelectItem>
                            {jurisdictions.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label htmlFor="pg-model" className={labelCls}>Business model *</label>
                        <Select value={form.business_model} onValueChange={set("business_model")}>
                          <SelectTrigger id="pg-model" className={inputCls} data-testid="pg-model"><SelectValue placeholder="Select business model" /></SelectTrigger>
                          <SelectContent>{BUSINESS_MODELS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <fieldset>
                      <legend className={labelCls}>Support required * <span className="normal-case font-normal">(select all that apply)</span></legend>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {SUPPORT_OPTIONS.map((o) => (
                          <label key={o} className={`flex items-center gap-2 rounded-sm border px-3 py-2 text-sm cursor-pointer transition-colors ${form.support_required.includes(o) ? "border-orange-400 bg-orange-50 text-orange-700" : "border-border bg-background text-muted-foreground"}`} data-testid={`pg-support-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            <input type="checkbox" checked={form.support_required.includes(o)} onChange={() => toggleArr("support_required", o)} className="h-3.5 w-3.5 accent-orange-600" /> {o}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    <div>
                      <label htmlFor="pg-activity" className={labelCls}>Short description of business activity *</label>
                      <Textarea id="pg-activity" value={form.business_activity} onChange={set("business_activity")} maxLength={600} placeholder="What do you sell, to whom, and in which countries?" className="mt-2 min-h-[90px] bg-background border-border" data-testid="pg-activity" />
                    </div>

                    <p className="text-[11px] uppercase tracking-widest text-muted-foreground pt-2">Optional — helps us assess faster</p>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="pg-company" className={labelCls}>Company / business name</label>
                        <Input id="pg-company" value={form.company_name} onChange={set("company_name")} maxLength={140} className={inputCls} data-testid="pg-company" />
                      </div>
                      <div>
                        <label htmlFor="pg-website" className={labelCls}>Website or app URL</label>
                        <div className="flex items-center gap-3">
                          <Input id="pg-website" value={form.website_url} onChange={set("website_url")} disabled={form.website_not_live} maxLength={200} placeholder="https://…" className={inputCls} data-testid="pg-website" />
                          <label className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap cursor-pointer" data-testid="pg-not-live">
                            <input type="checkbox" checked={form.website_not_live} onChange={(e) => setForm((s) => ({ ...s, website_not_live: e.target.checked }))} className="h-3.5 w-3.5 accent-orange-600" /> Not live yet
                          </label>
                        </div>
                      </div>
                      <div>
                        <label htmlFor="pg-markets" className={labelCls}>Target customer markets</label>
                        <Input id="pg-markets" value={form.target_markets} onChange={set("target_markets")} maxLength={200} placeholder="e.g. EU, UK, Southeast Asia" className={inputCls} data-testid="pg-markets" />
                      </div>
                      <div>
                        <label htmlFor="pg-currencies" className={labelCls}>Transaction currencies</label>
                        <Input id="pg-currencies" value={form.currencies} onChange={set("currencies")} maxLength={120} placeholder="e.g. USD, EUR" className={inputCls} data-testid="pg-currencies" />
                      </div>
                      <div>
                        <label htmlFor="pg-volume" className={labelCls}>Expected monthly processing volume</label>
                        <div className="flex gap-2">
                          <Select value={form.monthly_volume} onValueChange={set("monthly_volume")}>
                            <SelectTrigger id="pg-volume" className={inputCls} data-testid="pg-volume"><SelectValue placeholder="Select volume" /></SelectTrigger>
                            <SelectContent>{VOLUMES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                          </Select>
                          <Select value={form.volume_currency} onValueChange={set("volume_currency")}>
                            <SelectTrigger className={`${inputCls} w-28 shrink-0`} data-testid="pg-volume-currency"><SelectValue /></SelectTrigger>
                            <SelectContent>{VOLUME_CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <label htmlFor="pg-platform" className={labelCls}>Website / platform</label>
                        <Select value={form.platform} onValueChange={set("platform")}>
                          <SelectTrigger id="pg-platform" className={inputCls} data-testid="pg-platform"><SelectValue placeholder="Select platform" /></SelectTrigger>
                          <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="sm:col-span-2">
                        <label htmlFor="pg-provider" className={labelCls}>Existing or preferred payment provider</label>
                        <Input id="pg-provider" value={form.existing_provider} onChange={set("existing_provider")} maxLength={120} className={inputCls} data-testid="pg-provider" />
                      </div>
                    </div>
                    <fieldset>
                      <legend className={labelCls}>Required payment capabilities</legend>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {CAPABILITIES.map((c) => (
                          <label key={c} className={`flex items-center gap-2 rounded-sm border px-3 py-2 text-sm cursor-pointer transition-colors ${form.capabilities.includes(c) ? "border-orange-400 bg-orange-50 text-orange-700" : "border-border bg-background text-muted-foreground"}`} data-testid={`pg-cap-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            <input type="checkbox" checked={form.capabilities.includes(c)} onChange={() => toggleArr("capabilities", c)} className="h-3.5 w-3.5 accent-orange-600" /> {c}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    <p className="text-xs text-muted-foreground leading-relaxed">Please do not include API keys, passwords, card numbers or bank credentials in this form. If supporting documents are needed, we will request them later through our existing secure document process.</p>
                  </div>
                )}

                {step === 2 && (
                  <div className="mt-6 space-y-5">
                    <div className="rounded-sm border border-border bg-secondary/40 p-4" data-testid="pg-review">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Your requirements</p>
                        <button type="button" onClick={() => goStep(1)} className="text-xs text-orange-700 hover:underline" data-testid="pg-edit">Back / Edit</button>
                      </div>
                      <dl className="mt-3 space-y-1.5">
                        {reviewRows.map(([k, v]) => (
                          <div key={k} className="flex gap-3 text-sm"><dt className="w-44 shrink-0 text-muted-foreground">{k}</dt><dd className="text-foreground break-words">{v}</dd></div>
                        ))}
                      </dl>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="pg-name" className={labelCls}>Full name *</label>
                        <Input id="pg-name" value={form.name} onChange={set("name")} maxLength={120} className={inputCls} data-testid="pg-name" />
                      </div>
                      <div>
                        <label htmlFor="pg-email" className={labelCls}>Email *</label>
                        <Input id="pg-email" type="email" value={form.email} onChange={set("email")} maxLength={200} className={inputCls} data-testid="pg-email" />
                      </div>
                      <div>
                        <label htmlFor="pg-country" className={labelCls}>Country of residence *</label>
                        <Input id="pg-country" value={form.country} onChange={set("country")} maxLength={80} className={inputCls} data-testid="pg-country" />
                      </div>
                      <div>
                        <label htmlFor="pg-phone" className={labelCls}>Phone (with country code)</label>
                        <Input id="pg-phone" value={form.phone} onChange={set("phone")} maxLength={40} placeholder="+852 …" className={inputCls} data-testid="pg-phone" />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="pg-message" className={labelCls}>Additional requirements / message</label>
                      <Textarea id="pg-message" value={form.message} onChange={set("message")} maxLength={1000} className="mt-2 min-h-[80px] bg-background border-border" data-testid="pg-message" />
                    </div>
                    {/* Honeypot */}
                    <input type="text" tabIndex={-1} autoComplete="off" value={form.hp} onChange={set("hp")} className="hidden" aria-hidden="true" data-testid="pg-hp" />
                    <label className="flex items-start gap-3 cursor-pointer text-sm text-muted-foreground">
                      <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" data-testid="pg-consent" />
                      <span>I agree to the <Link to="/legal/privacy" className="text-orange-700 hover:underline">Privacy Policy</Link> and consent to UNICORP contacting me about my enquiry. *</span>
                    </label>
                  </div>
                )}

                {error && <p role="alert" aria-live="assertive" className="mt-4 rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="pg-error">{error}</p>}

                <div className="mt-6 flex items-center gap-3">
                  {step === 2 && (
                    <Button type="button" variant="outline" className="rounded-sm border-slate-300 bg-card hover:bg-secondary" onClick={() => goStep(1)} data-testid="pg-back"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back</Button>
                  )}
                  {step === 1 ? (
                    <Button type="button" className="rounded-sm bg-orange-600 text-white hover:bg-orange-700 h-11 px-7" onClick={next} data-testid="pg-next">Continue <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
                  ) : (
                    <Button type="submit" disabled={busy} className="rounded-sm bg-orange-600 text-white hover:bg-orange-700 h-11 px-7 disabled:opacity-50" data-testid="pg-submit">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Payment Assessment Request"}
                    </Button>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Compliance disclaimer */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        <div className="rounded-sm border border-border bg-secondary/40 p-5 flex items-start gap-3 text-sm text-muted-foreground" data-testid="pg-disclaimer">
          <ShieldCheck className="h-4 w-4 text-orange-600 mt-0.5 shrink-0" />
          <span>{disclaimer}</span>
        </div>
      </section>

      <Footer />
    </div>
  );
}
